<script setup lang="ts">
import { HttpClientMethod } from '@baarbaanet/http-client/dist/types'
import { raw } from '@barbanet/api-service'

import { onMounted, ref, watch } from 'vue'

import { BModal } from '@barbanet/ui-kit/components'
import { useExceptionHandler } from '@barbanet/ui-kit/composables'

import { useUserStatusCheck } from '../composables'
import { useUiInstructionStore } from '../stores/ui-instruction-store'
import { UiInstructionButton } from '../types'

interface ModalConfig {
	visible: boolean
	body?: string
	bind: {
		title?: string
		submitText?: string
		dismissText?: string
		secondaryText?: string
		noFooter?: boolean
		closeIcon?: boolean
	}
	events: {
		submit?: () => Promise<(() => void) | undefined>
		dismiss?: () => Promise<(() => void) | undefined>
		secondaryClick?: () => Promise<(() => void) | undefined>
	}
}

const uiInstructionStore = useUiInstructionStore()
const userStatusCheck = useUserStatusCheck()

const modalConfig = ref<Nullable<ModalConfig>>(null)
const loading = ref(false)

const getButtonAction = async (button: UiInstructionButton) => {
	switch (button.type) {
		case 0:
			return () => {
				console.warn('[getButtonAction] No action')
			}
		case 1:
			return () => {
				// TODO: handle application environment
				if (button.uri) {
					window.location.href = button.uri
				}
			}
		case 2:
			if (
				!button.httpMethod ||
				!['GET', 'POST', 'PUT'].includes(button.httpMethod) ||
				!button.uri
			) {
				console.warn('[getButtonAction] httpMethod or uri is not acceptable')
				return
			}

			loading.value = true
			try {
				const response = await raw(
					button.httpMethod as HttpClientMethod,
					button.uri,
					{ data: button.payload || {} }
				)

				if (response && response.data) {
					if (
						response.data &&
						'url' in (response.data.content as Object) &&
						typeof (response.data.content as { url: unknown }).url === 'string'
					) {
						// TODO: handle application environment
						window.location.href = (
							response.data.content as { url: string }
						).url
					} else {
						console.warn('[getButtonAction] url not provided in api response')
					}
				} else {
					console.warn('[getButtonAction] api response is not provided')
				}
			} catch (error) {
				useExceptionHandler(error)
			} finally {
				loading.value = false
			}
			break
		default:
			console.warn('[getButtonAction] NOT IMPLEMENTED')
			return () => {
				console.warn('[getButtonAction] NOT IMPLEMENTED')
			}
	}
}

const showUi = () => {
	const instruction = uiInstructionStore.getAndReset()

	if (!instruction) {
		return
	}

	switch (instruction.actionType) {
		case 1:
			modalConfig.value = {
				visible: false,
				body: instruction.body || undefined,
				bind: {
					title: instruction.title || undefined
				},
				events: {}
			}

			if (instruction.buttons && instruction.buttons.length) {
				const [submitButton, dismissButton, secondaryButton] =
					instruction.buttons

				modalConfig.value.bind.submitText = submitButton.text
				modalConfig.value.events.submit = () => getButtonAction(submitButton)

				if (dismissButton) {
					modalConfig.value.bind.dismissText = dismissButton.text
					modalConfig.value.events.dismiss = () =>
						getButtonAction(dismissButton)
				}

				if (secondaryButton) {
					modalConfig.value.bind.secondaryText = secondaryButton.text
					modalConfig.value.events.secondaryClick = () =>
						getButtonAction(secondaryButton)
				}
			} else {
				modalConfig.value.bind.noFooter = true
				modalConfig.value.bind.closeIcon = true
			}

			setTimeout(() => {
				if (modalConfig.value) {
					modalConfig.value.visible = true
				}
			}, 300)
			break
		default:
			console.warn('[showUi] NOT IMPLEMENTED')
	}
}

watch(
	() => uiInstructionStore.newInstruction,
	(isNew) => {
		if (isNew) {
			showUi()
		}
	},
	{ immediate: true }
)

onMounted(() => {
	if (userStatusCheck.canCheck()) {
		userStatusCheck.check()
	}
})
</script>

<template>
	<BModal
		v-if="modalConfig"
		v-model="modalConfig.visible"
		v-bind="modalConfig.bind"
		:submit-loading="loading"
		:dismiss-loading="loading"
		:secondary-loading="loading"
		persistent
		v-on="modalConfig.events"
	>
		<template
			v-if="modalConfig.body"
			#default
		>
			<p class="text-body-2 text-center text-on-surface-variant">
				{{ modalConfig.body }}
			</p>
		</template>
	</BModal>
</template>
